import React, { useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ModuleI } from "../../types";
import Container from "../../componentLibrary/Container";
import { useGetTopics } from "../../api/queries";
import Spinner from "../../componentLibrary/Spinner";
import { usePermissions } from "../../contexts/PermissionContext";
import TopicsList from "./components/ContentTopicsList";
import EditTopicComponent from "./components/EditTopicComponent";
import AddTopicComponent from "./components/AddTopicComponent";

const ContentManagementPage = () => {
  const [moduleData, setModuleData] = useState<ModuleI>();
  const [isEdit, setIsEdit] = useState(false);
  const [showAddModulePanel, setShowAddModulePanel] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  const { data: modules, isError, isLoading } = useGetTopics();
  const { canCreateContent, canUpdateContent } = usePermissions();

  const location = useLocation();
  const moduleId = location.state?.moduleId;

  React.useEffect(() => {
    if (moduleId && modules) {
      const module = modules.find((m) => m._id === moduleId);
      if (module) {
        setModuleData(module);
      }
    }
  }, [moduleId, modules]);

  // Scroll to editor on mobile when a module is selected
  const scrollToEditor = useCallback(() => {
    // Only scroll on mobile (when grid is single column)
    if (window.innerWidth < 1024 && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  if (isLoading) return <Spinner className="mx-auto" />;
  if (isError || !modules) return <div>Error</div>;

  return (
    <div className="p-2 sm:p-4">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Container className="lg:col-span-4 self-start max-h-[83vh] overflow-y-auto">
          <TopicsList
            modules={modules}
            moduleId={moduleData?._id}
            setModuleData={setModuleData}
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            canCreateContent={canCreateContent}
            showAddModulePanel={showAddModulePanel}
            setShowAddModulePanel={setShowAddModulePanel}
            onModuleSelect={scrollToEditor}
          />
        </Container>
        <div
          ref={editorRef}
          className="lg:col-span-8 flex flex-col h-full space-y-4"
        >
          {moduleData && !showAddModulePanel ? (
            <EditTopicComponent
              originalModuleData={moduleData}
              isEdit={isEdit}
              setIsEdit={setIsEdit}
              canUpdateContent={canUpdateContent}
              canCreateContent={canCreateContent}
            />
          ) : (
            <AddTopicComponent
              canCreateContent={canCreateContent}
              setShowAddModulePanel={setShowAddModulePanel}
              setModuleData={setModuleData}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentManagementPage;
